import React, { useState, useEffect } from 'react';
import {
    Paper,
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    MenuItem,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Stack,
    Divider,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import expenseApi from '../api/expenseApi';
import { PhoneAndroid, AccountBalance, Edit } from '@mui/icons-material';

const ExpenseForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const userId = expenseApi.getCurrentUserId();
    const [formData, setFormData] = useState({
        user_id: userId,
        title: '',
        description: '',
        amount: '',
        category: '',
        payment_method: 'mobile_money',
        source: 'manual', // Added source field
        date: format(new Date(), 'yyyy-MM-dd'),
        is_recurring: false,
        recurring_frequency: '',
        receipt_url: '',
        metadata: {},
    });

    const categories = [
        'Airtime',
        'PayBill',
        'Merchant Payment',
        'Transfer',
        'Internet & Data',
        'Customer Payment',
        'Service Charges',
        'M-Pesa Transactions',
        'Food',
        'Transport',
        'Utilities',
        'Entertainment',
        'Shopping',
        'Healthcare',
        'Education',
        'Other',
    ];

    const paymentMethods = [
        { value: 'mobile_money', label: 'Mobile Money' },
        { value: 'cash', label: 'Cash' },
        { value: 'bank', label: 'Bank Transfer' },
        { value: 'card', label: 'Card' },
        { value: 'other', label: 'Other' },
    ];

    const sourceOptions = [
        { value: 'manual', label: 'Manual Entry', icon: <Edit sx={{ fontSize: 18, color: '#FF9800' }} /> },
        { value: 'mpesa', label: 'M-Pesa', icon: <PhoneAndroid sx={{ fontSize: 18, color: '#4CAF50' }} /> },
        { value: 'bank', label: 'Bank', icon: <AccountBalance sx={{ fontSize: 18, color: '#1976d2' }} /> },
    ];

    useEffect(() => {
        if (id) {
            fetchExpense();
        }
    }, [id]);

    const fetchExpense = async () => {
        setLoading(true);
        try {
            const response = await expenseApi.getExpenseById(id);
            const expense = response.data.data;
            setFormData({
                ...expense,
                amount: parseFloat(expense.amount).toString(),
                date: format(new Date(expense.date), 'yyyy-MM-dd'),
                source: expense.source || 'manual',
            });
        } catch (error) {
            console.error('Error fetching expense:', error);
            toast.error('Failed to load expense');
            navigate('/admin/expenses');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);

        try {
            const data = {
                ...formData,
                amount: parseFloat(formData.amount),
                user_id: userId,
                source: formData.source || 'manual',
            };

            if (id) {
                await expenseApi.updateExpense(id, data);
                toast.success('Expense updated successfully');
            } else {
                await expenseApi.createExpense(data);
                toast.success('Expense created successfully');
            }
            navigate('/admin/expenses');
        } catch (error) {
            console.error('Error saving expense:', error);
            toast.error(error.response?.data?.message || 'Failed to save expense');
        } finally {
            setSubmitting(false);
        }
    };

    const getSourceLabel = (source) => {
        const option = sourceOptions.find(s => s.value === source);
        return option ? option.label : 'Manual Entry';
    };

    const getSourceIcon = (source) => {
        const option = sourceOptions.find(s => s.value === source);
        return option ? option.icon : null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom>
                {id ? 'Edit Expense' : 'Add New Expense'}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {id ? 'Update existing expense details' : 'Create a new expense record'}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Title */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter expense title"
                            variant="outlined"
                        />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            placeholder="Enter description (optional)"
                            variant="outlined"
                        />
                    </Grid>

                    {/* Amount */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Amount (KES)"
                            name="amount"
                            type="number"
                            value={formData.amount}
                            onChange={handleChange}
                            placeholder="0.00"
                            variant="outlined"
                            InputProps={{
                                startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>KES</Typography>,
                            }}
                        />
                    </Grid>

                    {/* Category */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            select
                            label="Category"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            variant="outlined"
                        >
                            {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Payment Method */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Payment Method"
                            name="payment_method"
                            value={formData.payment_method}
                            onChange={handleChange}
                            select
                            variant="outlined"
                        >
                            {paymentMethods.map((method) => (
                                <MenuItem key={method.value} value={method.value}>
                                    {method.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Date */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label="Date"
                            name="date"
                            type="date"
                            value={formData.date}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                        />
                    </Grid>

                    {/* Source */}
                    <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Source</InputLabel>
                            <Select
                                name="source"
                                value={formData.source || 'manual'}
                                onChange={handleChange}
                                label="Source"
                            >
                                {sourceOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {option.icon}
                                            {option.label}
                                            {option.value === 'manual' && (
                                                <Chip
                                                    size="small"
                                                    label="Manual"
                                                    variant="outlined"
                                                    sx={{ ml: 1, fontSize: '0.6rem', height: 20 }}
                                                />
                                            )}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formData.source === 'manual'
                                ? 'This expense was entered manually'
                                : `This expense was synced from ${getSourceLabel(formData.source)}`}
                        </Typography>
                    </Grid>

                    {/* Recurring */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_recurring}
                                    onChange={handleChange}
                                    name="is_recurring"
                                    color="primary"
                                />
                            }
                            label="Is Recurring Expense?"
                        />
                    </Grid>

                    {/* Recurring Frequency */}
                    {formData.is_recurring && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Recurring Frequency"
                                name="recurring_frequency"
                                value={formData.recurring_frequency || ''}
                                onChange={handleChange}
                                select
                                variant="outlined"
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="quarterly">Quarterly</MenuItem>
                                <MenuItem value="yearly">Yearly</MenuItem>
                            </TextField>
                        </Grid>
                    )}

                    {/* Receipt URL */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Receipt URL"
                            name="receipt_url"
                            value={formData.receipt_url || ''}
                            onChange={handleChange}
                            placeholder="https://example.com/receipt.jpg"
                            variant="outlined"
                            helperText="Optional: Link to receipt image or document"
                        />
                    </Grid>
                </Grid>

                {/* Current Source Badge (for editing) */}
                {id && formData.source && formData.source !== 'manual' && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                                ⚠️ This expense was synced from {getSourceLabel(formData.source)}.
                                Editing will mark it as manually updated.
                            </Typography>
                            <Chip
                                size="small"
                                label="Synced"
                                color="info"
                            />
                        </Stack>
                    </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        startIcon={submitting && <CircularProgress size={20} />}
                        sx={{ minWidth: 150 }}
                    >
                        {submitting ? 'Saving...' : id ? 'Update Expense' : 'Create Expense'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/admin/expenses')}
                    >
                        Cancel
                    </Button>
                    {id && (
                        <Button
                            variant="text"
                            color="error"
                            onClick={() => {
                                if (window.confirm('Are you sure you want to delete this expense?')) {
                                    // Add delete functionality here
                                }
                            }}
                            sx={{ ml: 'auto' }}
                        >
                            Delete
                        </Button>
                    )}
                </Box>
            </form>
        </Paper>
    );
};

export default ExpenseForm;